import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  FiArrowRight,
  FiBookOpen,
  FiEdit3,
  FiGitMerge,
  FiCompass,
  FiGrid,
  FiShare2,
  FiSmartphone,
  FiRotateCw,
  FiArrowUpRight,
  FiPlay,
} from "react-icons/fi";
import "./home.css";

const Home = () => {
  // Scroll animation trigger
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 },
    );

    const animElements = document.querySelectorAll(".animate-on-scroll");
    animElements.forEach((el) => observer.observe(el));

    return () => {
      animElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // For the active step in "More than a PDF reader"
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      title: "Read",
      desc: "Open multiple source documents side-by-side on your workspace canvas.",
    },
    {
      title: "Extract",
      desc: "Highlight passages, crop figures, and pull key sentences onto the workspace.",
    },
    {
      title: "Connect",
      desc: "Draw fluid, graphical connector lines linking different notes and files.",
    },
    {
      title: "Understand",
      desc: "Organize disparate findings into structured mind-maps and cohesive summaries.",
    },
    {
      title: "Share",
      desc: "Export your synthesized workspace reports and collaborate with team members.",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [steps.length]);

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant", // or "smooth"
    });
  }, [pathname]);

  return (
    <div className="homepage-wrapper">
      {/* 1. HERO SECTION */}
      <section className="hero-section">
        <div className="container hero-container">
          <div className="hero-content animate-on-scroll fade-up">
            <span className="badge-pill">Productivity Redefined</span>
            <h1 className="hero-title">
              A Better Way to <br />
              <span className="highlight-text">Work With Documents</span>
            </h1>
            <p className="hero-subtitle">
              Your Documents. Your Ideas. One Connected Workspace.
            </p>
            <p className="hero-description">
              Traditional document workflows force you to jump between PDF
              readers, note-taking apps, folders, and separate tools.{" "}
              <strong>LiquidText</strong> brings everything together. Read your
              source documents while capturing notes, extracting info, creating
              connections, and organizing your thinking.
            </p>

            <div className="hero-cta-group">
              <a href="#get-started" className="btn-primary">
                Get Started Free <FiArrowRight className="btn-icon" />
              </a>
              <button
                className="btn-secondary"
                onClick={() => alert("Watch demo feature coming soon!")}
              >
                <FiPlay className="btn-icon-play" /> Watch Demo
              </button>
            </div>

            <div className="hero-bullets">
              <div className="bullet-item">
                <span className="bullet-dot"></span> Work smarter.
              </div>
              <div className="bullet-item">
                <span className="bullet-dot"></span> Understand more.
              </div>
              <div className="bullet-item">
                <span className="bullet-dot"></span> Keep the context.
              </div>
            </div>
          </div>

          <div className="hero-visual-wrapper animate-on-scroll fade-left">
            <div className="hero-workspace-mockup">
              <img
                src="/images/hero_workspace.png"
                alt="LiquidText Workspace Demonstration"
                className="hero-img img-fluid"
              />
              <div className="floating-badge badge-1">
                <span className="floating-icon">💡</span>
                <div>
                  <strong>Workspace Active</strong>
                  <small>Project Nebula</small>
                </div>
              </div>
              <div className="floating-badge badge-2">
                <span className="floating-icon">🔗</span>
                <div>
                  <strong>12 Connections</strong>
                  <small>Across 3 Source Files</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. MORE THAN A PDF READER */}
      <section className="contrast-section">
        <div className="container">
          <div className="section-header text-center animate-on-scroll fade-up">
            <span className="section-tag">Beyond Static Reading</span>
            <h2>More Than a PDF Reader</h2>
            <p className="section-desc max-600">
              Documents Become Knowledge. A PDF reader lets you read a document.
              LiquidText lets you work with it.
            </p>
          </div>

          <div className="contrast-grid">
            {/* Traditional Reader */}
            <div className="contrast-card traditional animate-on-scroll fade-right">
              <div className="card-pill text-red">Traditional PDF Reader</div>
              <h3>Static Reading</h3>
              <p>
                Forces you to scroll through pages in a restricted vertical
                flow, separate from your notebook and brainstorm board.
              </p>
              <div className="contrast-image-container">
                <img
                  src="/images/pdf_reader.png"
                  alt="Traditional PDF Reader Interface"
                  className="contrast-img img-fluid"
                />
              </div>
              <div className="card-footer-note">
                🚫 Scattered Notes • Disconnected Context • Hard to Compare
              </div>
            </div>

            {/* LiquidText Workspace */}
            <div className="contrast-card active-liquidtext animate-on-scroll fade-left">
              <div className="card-pill text-green">LiquidText Experience</div>
              <h3>Active Knowledge Building</h3>
              <p>
                Bring documents into a workspace, pull out important passages,
                write observations, connect ideas, compare info, and build
                visual networks.
              </p>
              <div className="contrast-image-container">
                <img
                  src="/images/liquidtext_active.png"
                  alt="LiquidText Interactive Document Workspace"
                  className="contrast-img img-fluid"
                />
              </div>
              <div className="card-footer-note">
                ✨ Live Connectors • Pinched Pages • Synced Context
              </div>
            </div>
          </div>

          {/* Animate-through progress flow */}
          <div className="workflow-section animate-on-scroll fade-up">
            <h4 className="workflow-title text-center">
              The Fluid Research Flow
            </h4>
            <div className="workflow-bar">
              {steps.map((step, idx) => (
                <div
                  key={idx}
                  className={`workflow-step ${idx === activeStep ? "active" : ""}`}
                  onClick={() => setActiveStep(idx)}
                >
                  <span className="step-num">0{idx + 1}</span>
                  <span className="step-name">{step.title}</span>
                </div>
              ))}
            </div>
            <div className="workflow-details-card">
              <p className="workflow-desc">
                <strong>{steps[activeStep].title}:</strong>{" "}
                {steps[activeStep].desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. WHY LIQUIDTEXT? SECTION */}
      <section className="features-section">
        <div className="container">
          <div className="section-header text-center animate-on-scroll fade-up">
            <span className="section-tag">Powerful Features</span>
            <h2>Why LiquidText?</h2>
            <p className="section-desc max-600">
              Designed to help you comprehend faster, synthesize complex ideas,
              and keep everything connected.
            </p>
          </div>

          {/* Grid Layout containing features paired with images */}
          <div className="features-grid">
            {/* Feature 1 */}
            <div className="feature-item-card animate-on-scroll fade-up">
              <div className="feature-icon-wrapper blue-icon">
                <FiBookOpen />
              </div>
              <h3>Read Faster</h3>
              <p>
                Review lengthy documents efficiently and quickly find the
                information that matters. Pinch pages to bring search terms
                directly together.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="feature-item-card animate-on-scroll fade-up">
              <div className="feature-icon-wrapper green-icon">
                <FiEdit3 />
              </div>
              <h3>Capture Important Information</h3>
              <p>
                Highlight passages, extract excerpts, write notes, and collect
                important facts directly on your infinite workspace as you read.
              </p>
            </div>

            {/* Feature 3 - Interactive Image Placement */}
            <div className="feature-showcase-image animate-on-scroll fade-right">
              <img
                src="/images/why_read_faster.png"
                alt="Pinching documents and active reading view in LiquidText"
                className="showcase-img"
              />
              <div className="image-caption">
                <span>
                  Touch & gesture based interface lets you squeeze documents to
                  focus on what matters.
                </span>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="feature-item-card animate-on-scroll fade-up">
              <div className="feature-icon-wrapper purple-icon">
                <FiGitMerge />
              </div>
              <h3>Connect Your Ideas</h3>
              <p>
                Create visual connections between notes, excerpts, and source
                material to understand complex relationships across your
                research.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="feature-item-card animate-on-scroll fade-up">
              <div className="feature-icon-wrapper orange-icon">
                <FiCompass />
              </div>
              <h3>Keep Context</h3>
              <p>
                Jump directly from a note or connection back to its original
                location in the source document. Never lose track of where an
                idea came from.
              </p>
            </div>

            {/* Feature 6 - Interactive Image Placement */}
            <div className="feature-showcase-image animate-on-scroll fade-left">
              <img
                src="/images/why_connect_ideas.png"
                alt="Connecting ideas in the workspace canvas"
                className="showcase-img"
              />
              <div className="image-caption">
                <span>
                  Drag excerpts together to automatically link, or draw lines
                  with your stylus.
                </span>
              </div>
            </div>

            {/* Feature 7 */}
            <div className="feature-item-card animate-on-scroll fade-up">
              <div className="feature-icon-wrapper indigo-icon">
                <FiGrid />
              </div>
              <h3>Organize Your Thinking</h3>
              <p>
                Turn scattered information into structured ideas, research,
                reports, and conclusions with visual mindmaps and flexible
                workspaces.
              </p>
            </div>

            {/* Feature 8 */}
            <div className="feature-item-card animate-on-scroll fade-up">
              <div className="feature-icon-wrapper teal-icon">
                <FiShare2 />
              </div>
              <h3>Share & Collaborate</h3>
              <p>
                Share your work and collaborate with colleagues, clients,
                researchers, and other professionals with fully interactive
                exported workspace files.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. WORK ACROSS DEVICES */}
      <section className="devices-section">
        <div className="container">
          <div className="devices-layout">
            <div className="devices-content animate-on-scroll fade-right">
              <span className="section-tag-light">Universal Workflow</span>
              <h2>Your Workflow, Wherever You Work</h2>
              <p>
                LiquidText supports modern desktop and tablet workflows,
                including iPadOS, Windows, and macOS. It supports touch, pen and
                ink, mouse, trackpad, and multi-monitor setups.
              </p>
              <div className="tech-pills">
                <span className="tech-pill">
                  <FiSmartphone /> iPadOS Touch
                </span>
                <span className="tech-pill">✍️ Pen & Ink</span>
                <span className="tech-pill">🖱️ Mouse & Trackpad</span>
                <span className="tech-pill">🖥️ Multi-Monitor</span>
              </div>
              <a href="#platforms" className="btn-primary btn-margin-top">
                Explore Platforms <FiArrowUpRight />
              </a>
            </div>

            <div className="devices-visual animate-on-scroll fade-left">
              <div className="devices-mockup-wrapper">
                <img
                  src="/images/work_across_devices.png"
                  alt="LiquidText running on Macbook, iPad, and Windows desktop"
                  className="devices-img img-fluid"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. LIQUIDTEXT LIVE */}
      <section className="live-sync-section">
        <div className="container">
          <div className="live-sync-card animate-on-scroll fade-up">
            <div className="live-sync-content">
              <span className="live-badge">
                <FiRotateCw className="live-icon-spin" /> LiquidText LIVE
              </span>
              <h2>Your Work, Synced Everywhere</h2>
              <p className="lead-text">
                With LiquidText LIVE, your projects can synchronize across
                devices while providing continuous cloud backup.
              </p>
              <p className="sub-text">
                Start working on your iPad during your commute, review
                connections on your macOS laptop, and compile final conclusions
                on your Windows workstation. Synchronization is instant,
                automatic, and fully secure.
              </p>
              <div className="live-features">
                <div className="live-feature-bullet">
                  <div className="bullet-circle">✓</div>
                  <span>Real-time project synchronization</span>
                </div>
                <div className="live-feature-bullet">
                  <div className="bullet-circle">✓</div>
                  <span>Continuous cloud backup</span>
                </div>
              </div>
              <a href="#discover-live" className="btn-live">
                Discover LiquidText LIVE <FiArrowRight />
              </a>
            </div>

            {/* Visual element using workspace as context for LIVE */}
            <div className="live-sync-visual">
              <div className="sync-glow-box">
                <div className="device-icon-box ipad-icon-box">iPad</div>
                <div className="sync-arrows">⇄</div>
                <div className="device-icon-box desktop-icon-box">Desktop</div>
                <div className="cloud-indicator">Cloud Sync Active</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FINAL CTA */}
      <section id="get-started" className="cta-section">
        <div className="cta-container container text-center animate-on-scroll fade-up">
          <span className="cta-sub">Elevate Your Research</span>
          <h2 className="cta-title">Ready to Change the Way You Read?</h2>
          <p className="cta-desc max-600">
            Stop switching between disconnected tools. Bring your documents,
            notes, ideas, and research together with LiquidText.
          </p>
          <div className="cta-buttons">
            <a href="#get-started" className="btn-cta-primary">
              Get Started Now
            </a>
          </div>
          <div className="cta-footnote">
            Available on iPadOS, macOS, and Windows 10/11. No credit card
            required.
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
